import { UI, Utils } from 'common';
import React, { useState } from 'react';
import CostBreakdown from './CostBreakdown.component';
import { ConfirmationState } from './hooks/useSubscriptionPlanState';
import { isInTrialBillingPeriod } from './planRules';

const { WrappedButton, WrappedButtonType, Typography } = UI;
const { BillingPlans, Util } = Utils;

interface PlanSummaryCardProps {
	isSeatOnlyChange: boolean;
	planName: string;
	planPrice: number;
	selectedSeats: number;
	includedSeats: number;
	additionalSeats: number;
	seatPrice: number;
	totalPrice: number;
	hasChanges: boolean;
	isOverflow: boolean;
	isMultiLocation: boolean;
	isDowngrade: boolean;
	isOnTrialPlan: boolean;
	currentPlanName: string;
	confirmationState: ConfirmationState;

	nextBillingDate: string;
	currentSeats: number;
	onConfirm: () => void;
	onReset: () => void;
	onReviewDeleteUsers?: () => void;
	onContactSupport?: () => void;
	readOnly?: boolean;
	isCancelled?: boolean;
	currentIsLegacy?: boolean;
	isNewLineup?: boolean;
}

const formatSeatsText = (count: number) =>
	`${count} user ${count === 1 ? 'seat' : 'seats'}`;

const getSummaryText = ({
	isSeatOnlyChange,
	hasChanges,
	planName,
	selectedSeats,
	currentSeats,
	formattedBillingDate,
	isDowngrade,
	isOverflow,
	isMultiLocation,
	isOnTrialPlan,
	inTrialBilling,
	currentPlanName,
	isNewLineup,
	currentIsLegacy,
}: {
	isSeatOnlyChange: boolean;
	hasChanges: boolean;
	planName: string;
	selectedSeats: number;
	currentSeats: number;
	formattedBillingDate: string;
	isDowngrade: boolean;
	isOverflow: boolean;
	isMultiLocation: boolean;
	isOnTrialPlan: boolean;
	inTrialBilling: boolean;
	currentPlanName: string;
	isNewLineup: boolean;
	currentIsLegacy: boolean;
}): string => {
	// On the Trial plan itself, no plan change — nudge toward Basic-at-trial-end
	if (isOnTrialPlan && !isSeatOnlyChange) {
		const seatsClause = isNewLineup
			? ` with ${formatSeatsText(currentSeats)}`
			: '';
		return `You're on the ${planName} plan${seatsClause}. If no plan is selected, you'll move to the Basic plan at the end of your trial.`;
	}

	// Multi-location is the same gating message in all flows
	if (isMultiLocation && hasChanges) {
		return `The Multi-Location plan is managed with our team. Contact support to switch and we'll help you get set up.`;
	}

	// Same-plan seat updates (only reachable when seats are editable — new-lineup)
	if (isSeatOnlyChange) {
		return "You're updating your user seats. Changes take effect immediately. Your new total is shown below and will appear on your next bill.";
	}

	// No changes
	if (!hasChanges) {
		const seatsClause = isNewLineup
			? ` with ${formatSeatsText(currentSeats)}`
			: '';
		if (inTrialBilling) {
			return `You're on the ${planName} plan${seatsClause}. Your total is shown below and will appear on your next bill.`;
		}
		return `You're on the ${planName} plan${seatsClause}.${formattedBillingDate ? ` Your next billing date is ${formattedBillingDate}.` : ''}`;
	}

	// Plan change — compose from parts. Per spec: downgrade body omits the seat
	// clause when seats aren't changing (3.4, 3.5, 3.6 all read "You're switching
	// to the X plan." with no seats); seat-change downgrades (3.11, 3.12) keep it.
	const seatsChanged = selectedSeats !== currentSeats;
	const includeSeats =
		isNewLineup && (!isDowngrade || seatsChanged || isOverflow);
	const seatsPart = (() => {
		if (!includeSeats) return '';
		return isOverflow
			? ' and updating your user seats'
			: ` with ${formatSeatsText(selectedSeats)}`;
	})();

	// Post-trial regular plans: upgrade phrasing is short ("...and appear on your next bill"),
	// downgrade keeps the explicit "Your new total is shown below..." sentence.
	// Switching from a legacy plan always takes effect immediately — there's no
	// shared billing cycle to ride out, so skip the downgrade "keep features
	// through…" timing copy.
	if (!inTrialBilling) {
		if (isDowngrade && !currentIsLegacy) {
			return `You're switching to the ${planName} plan${seatsPart}. You'll keep ${currentPlanName} features through ${formattedBillingDate}. Your new total is shown below and will appear on your next bill.`;
		}
		return `You're switching to the ${planName} plan${seatsPart}. Changes take effect immediately and appear on your next bill.`;
	}

	// Trial-billing window — keep the existing combined format
	const timingPart =
		isDowngrade && !currentIsLegacy
			? ` You will keep access to the advanced ${currentPlanName} features until the end of your trial on ${formattedBillingDate}.`
			: ' Changes take effect immediately.';
	return `You're switching to the ${planName} plan${seatsPart}.${timingPart} Your new total is shown below and will appear on your next bill.`;
};

const getLegalText = ({
	isFromTrial,
	inTrialBilling,
	hasChanges,
	isMultiLocation,
	formattedBillingDate,
	isCancelled,
	isOverflow,
}: {
	isFromTrial: boolean;
	inTrialBilling: boolean;
	hasChanges: boolean;
	isMultiLocation: boolean;
	formattedBillingDate: string;
	isCancelled: boolean;
	isOverflow: boolean;
}): string | null => {
	const date = formattedBillingDate || 'your trial end date';
	const phase1Prefix = `If no plan is selected by ${date} your subscription will be automatically moved to the Basic plan at $14.99/month.`;
	const trialCancel =
		'You can cancel anytime before then at no charge by clicking on the cancellation link below.';
	// In overflow the CTA opens the user-deletion modal as a "Continue" step
	// before the actual update — match the verb in the legal copy.
	const updateVerb: 'Continue' | 'Update' = isOverflow ? 'Continue' : 'Update';
	const trialRenew = (verb: 'Confirm' | 'Update' | 'Continue' | null) =>
		verb
			? `By clicking ${verb}, your subscription will automatically renew monthly at the price shown above starting ${date}.`
			: `Your subscription will automatically renew monthly at the price shown above starting ${date}.`;

	// Cancelled account — every plan reactivates via Confirm. Multi-Location
	// goes through a support flow, so it shows the plain renew copy without
	// the "By clicking Confirm" prefix.
	if (isCancelled) {
		// Cancelled from trial: starting {date} copy with "cancel anytime before
		// then" tail.
		if (isFromTrial || inTrialBilling) {
			return isMultiLocation
				? `${trialRenew(null)} ${trialCancel}`
				: `${trialRenew('Confirm')} ${trialCancel}`;
		}
		// Cancelled from regular paying plan: until-you-cancel copy.
		const regularRenew = (verb: 'Confirm' | null) =>
			verb
				? `By clicking ${verb}, your subscription will automatically renew monthly at the price shown above until you cancel.`
				: `Your subscription will automatically renew monthly at the price shown above until you cancel.`;
		const regularTail =
			'Cancellation stops future billing; accrued charges may still be billed. Upgrades are prorated. Cancel anytime by clicking the link below.';
		return isMultiLocation
			? `${regularRenew(null)} ${regularTail}`
			: `${regularRenew('Confirm')} ${regularTail}`;
	}

	// Phase 1 — on Trial401 with no committed plan; user is choosing what to confirm
	if (isFromTrial) {
		return isMultiLocation
			? `${phase1Prefix} ${trialCancel}`
			: `${phase1Prefix} ${trialRenew('Confirm')} ${trialCancel}`;
	}

	// Phase 2/3 — committed during trial billing window (pre-first-bill).
	// Multi-location goes through a support flow (no Update button), so always
	// show plain renew copy. For other plans: "By clicking Update" when changed.
	if (inTrialBilling) {
		return hasChanges && !isMultiLocation
			? `${trialRenew(updateVerb)} ${trialCancel}`
			: `${trialRenew(null)} ${trialCancel}`;
	}

	// Regular paying user — Update CTA active
	if (hasChanges && !isMultiLocation) {
		return `By clicking ${updateVerb}, your subscription will automatically renew monthly at the price shown above until you cancel. Cancellation stops future billing; accrued charges may still be billed. Upgrades are prorated. Cancel anytime by clicking the link below.`;
	}

	return null;
};

const getCtaProps = ({
	confirmationState,
	hasChanges,
	isMultiLocation,
	isOverflow,
	isFromTrial,
	isCancelled,
	onConfirm,
	onReviewDeleteUsers,
	onContactSupport,
}: {
	confirmationState: ConfirmationState;
	hasChanges: boolean;
	isMultiLocation: boolean;
	isOverflow: boolean;
	isFromTrial: boolean;
	isCancelled: boolean;
	onConfirm: () => void;
	onReviewDeleteUsers?: () => void;
	onContactSupport?: () => void;
}) => {
	// Cancelled accounts always reactivate via Confirm — even when the user is
	// sitting on the current plan with no changes.
	const useConfirm = isFromTrial || isCancelled;
	const primaryLabel = useConfirm ? 'Confirm' : 'Update';
	const progressLabel = useConfirm ? 'Confirming...' : 'Updating...';
	const successLabel = useConfirm ? 'Plan Confirmed!' : 'Plan Updated!';

	if (confirmationState === 'confirming') {
		return {
			label: progressLabel,
			buttonType: WrappedButtonType.Primary,
			disabled: true,
			dataqa: 'summary-updating',
		};
	}
	if (confirmationState === 'success') {
		return {
			label: successLabel,
			buttonType: WrappedButtonType.Primary,
			disabled: true,
			dataqa: 'summary-plan-updated',
			className: 'plan-summary-card__cta--success',
		};
	}
	if (!hasChanges && !isCancelled) {
		return {
			label: 'Update',
			buttonType: WrappedButtonType.Primary,
			disabled: true,
			dataqa: 'summary-current-plan',
		};
	}
	if (isMultiLocation) {
		return {
			label: 'Contact Support',
			buttonType: WrappedButtonType.Primary,
			onClick: onContactSupport,
			dataqa: 'summary-contact-support',
		};
	}
	if (isOverflow) {
		return {
			label: 'Continue',
			buttonType: WrappedButtonType.Primary,
			onClick: onReviewDeleteUsers || onConfirm,
			dataqa: 'summary-review-delete',
		};
	}
	return {
		label: primaryLabel,
		buttonType: WrappedButtonType.Primary,
		onClick: onConfirm,
		dataqa: isFromTrial ? 'summary-select-plan' : 'summary-update-plan',
	};
};

const PlanSummaryCard = ({
	isSeatOnlyChange,
	planName,
	planPrice,
	selectedSeats,
	includedSeats,
	additionalSeats,
	seatPrice,
	totalPrice,
	hasChanges,
	isOverflow,
	isMultiLocation,
	isDowngrade,
	isOnTrialPlan,
	currentPlanName,
	confirmationState,
	nextBillingDate,
	currentSeats,
	onConfirm,
	onReviewDeleteUsers,
	onContactSupport,
	readOnly = false,
	isCancelled = false,
	currentIsLegacy = false,
	isNewLineup: isNewLineupProp,
}: PlanSummaryCardProps) => {
	const isNewLineup = isNewLineupProp ?? BillingPlans.isNewLineupPlan();
	const formattedBillingDate = nextBillingDate
		? (Util as any).formatDateReadable(nextBillingDate)
		: '';

	const inTrialBilling = isInTrialBillingPeriod();
	const isFromTrial = isOnTrialPlan && inTrialBilling;

	const prettyPlanName = BillingPlans.prettyPrintPlan(planName);
	const prettyCurrentPlanName = BillingPlans.prettyPrintPlan(currentPlanName);

	// In the trial flow:
	//   - sitting on the current pending plan with no changes  → "Plan Summary"
	//   - the user has selected a different plan to confirm    → "Select Plan"
	// On a cancelled account every plan acts as a reactivation pick → "Select Plan".
	// Outside those, follow the standard Update / Plan Summary header.
	const headerTitle = isCancelled
		? 'Select Plan'
		: isFromTrial
			? hasChanges
				? 'Select Plan'
				: 'Plan Summary'
			: hasChanges
				? 'Update Plan'
				: 'Plan Summary';

	const bodyText = getSummaryText({
		isSeatOnlyChange,
		hasChanges,
		planName: prettyPlanName,
		selectedSeats,
		currentSeats,
		formattedBillingDate,
		isDowngrade,
		isOverflow,
		isMultiLocation,
		isOnTrialPlan,
		inTrialBilling,
		currentPlanName: prettyCurrentPlanName,
		isNewLineup,
		currentIsLegacy,
	});

	const ctaProps = getCtaProps({
		confirmationState,
		hasChanges: hasChanges || isFromTrial,
		isMultiLocation,
		isOverflow,
		isFromTrial,
		isCancelled,
		onConfirm,
		onReviewDeleteUsers,
		onContactSupport,
	});

	const legalText = getLegalText({
		isFromTrial,
		inTrialBilling,
		hasChanges,
		isMultiLocation,
		formattedBillingDate,
		isCancelled,
		isOverflow,
	});

	const [collapsed, setCollapsed] = useState(true);

	return (
		<>
			{!collapsed && (
				<div
					className="plan-summary-card__overlay"
					role="button"
					tabIndex={0}
					onClick={() => setCollapsed(true)}
					onKeyDown={e => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							setCollapsed(true);
						}
					}}
				/>
			)}
			<div
				className={`plan-summary-card${collapsed ? ' plan-summary-card--collapsed' : ''}`}
				onClick={collapsed ? () => setCollapsed(false) : undefined}
				{...(collapsed ? { role: 'button', tabIndex: 0 } : {})}
			>
				<div className="plan-summary-card__drag-handle" />
				<div
					role="button"
					tabIndex={0}
					className="plan-summary-card__header"
					onClick={() => setCollapsed(prev => !prev)}
					onKeyDown={e => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							setCollapsed(prev => !prev);
						}
					}}
				>
					<div className="plan-summary-card__header-info">
						<Typography.H3 className="plan-summary-card__header-title">
							{headerTitle}
						</Typography.H3>
					</div>
					{!readOnly && (
						<div className="plan-summary-card__header-actions">
							<WrappedButton {...ctaProps} />
						</div>
					)}
					<i
						className={`pi pi-chevron-${collapsed ? 'up' : 'down'} plan-summary-card__toggle`}
					/>
				</div>
				<hr className="plan-summary-card__divider" />
				<div className="plan-summary-card__collapsible">
					<div className="plan-summary-card__collapsible-inner">
						<div className="plan-summary-card__body">
							{!isFromTrial && !isCancelled && (
								<Typography.P className="plan-summary-card__description">
									{bodyText}
								</Typography.P>
							)}

							<CostBreakdown
								planName={prettyPlanName}
								planPrice={planPrice}
								includedSeats={includedSeats}
								additionalSeats={additionalSeats}
								seatPrice={seatPrice}
								totalPrice={totalPrice}
								formattedBillingDate={formattedBillingDate}
								isNewLineup={isNewLineup}
							/>
						</div>

						{!readOnly && (
							<div className="plan-summary-card__footer">
								<div className="plan-summary-card__actions">
									<WrappedButton {...ctaProps} />
								</div>
								{legalText && (
									<p
										className="plan-summary-card__legal"
										data-qa="summary-legal-text"
									>
										{legalText}
									</p>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
};

export default PlanSummaryCard;
